/**
 * Animates an element's height between 0 and its natural content height
 * (0.35s ease) to show or hide it. Operates on native DOM elements.
 */

const DURATION_MS = 350;
const TRANSITION = `height ${DURATION_MS}ms ease`;

interface CollapsibleElement extends HTMLElement
{
    __collapseShown?: boolean;
    __collapseCleanup?: () => void;
}

export class Collapse
{
    /** Set the initial visible/hidden state instantly, without animating. */
    static init(el: HTMLElement, shown: boolean): void
    {
        const e = el as CollapsibleElement;
        Collapse.cancel(e);
        e.__collapseShown = shown;
        e.style.transition = "";
        e.style.height = "";
        e.style.overflow = "";
        e.style.display = shown ? "block" : "none";
    }

    /** Animate the element open. */
    static show(el: HTMLElement): void
    {
        const e = el as CollapsibleElement;
        if (e.__collapseShown)
        {
            return;
        }
        Collapse.cancel(e);
        e.__collapseShown = true;

        e.style.display = "block";
        e.style.overflow = "hidden";
        const target = e.scrollHeight;
        e.style.height = "0px";
        // Force a reflow so the browser registers the starting height.
        void e.offsetHeight;
        e.style.transition = TRANSITION;
        e.style.height = `${target}px`;

        const onEnd = (event: TransitionEvent) =>
        {
            if (event.target !== e || event.propertyName !== "height")
            {
                return;
            }
            e.removeEventListener("transitionend", onEnd);
            e.__collapseCleanup = undefined;
            e.style.transition = "";
            e.style.height = "";
            e.style.overflow = "";
        };
        e.addEventListener("transitionend", onEnd);
        e.__collapseCleanup = () =>
        {
            e.removeEventListener("transitionend", onEnd);
            e.style.transition = "";
        };
    }

    /** Animate the element closed. */
    static hide(el: HTMLElement): void
    {
        const e = el as CollapsibleElement;
        if (e.__collapseShown === false && e.style.display === "none")
        {
            return;
        }
        Collapse.cancel(e);
        e.__collapseShown = false;

        e.style.height = `${e.scrollHeight}px`;
        e.style.overflow = "hidden";
        // Force a reflow so the transition starts from the current height.
        void e.offsetHeight;
        e.style.transition = TRANSITION;
        e.style.height = "0px";

        const onEnd = (event: TransitionEvent) =>
        {
            if (event.target !== e || event.propertyName !== "height")
            {
                return;
            }
            e.removeEventListener("transitionend", onEnd);
            e.__collapseCleanup = undefined;
            e.style.display = "none";
            e.style.transition = "";
            e.style.height = "";
            e.style.overflow = "";
        };
        e.addEventListener("transitionend", onEnd);
        e.__collapseCleanup = () =>
        {
            e.removeEventListener("transitionend", onEnd);
            e.style.transition = "";
        };
    }

    /** Toggle between open and closed. */
    static toggle(el: HTMLElement): void
    {
        if ((el as CollapsibleElement).__collapseShown)
        {
            Collapse.hide(el);
        }
        else
        {
            Collapse.show(el);
        }
    }

    private static cancel(e: CollapsibleElement): void
    {
        if (e.__collapseCleanup)
        {
            e.__collapseCleanup();
            e.__collapseCleanup = undefined;
        }
    }
}
